import type {
  NavigationPageItem,
  NavigationPageOption,
} from "#bb/types/blocks/navigation/NavigationPageOption";
import type { ConditionSource } from "#bb/lib/conditionalRules";
import { collectResourceConditionalRequirements } from "#bb/lib/conditionalRules";
import {
  resolveNavigationPageItem,
  type NavigationPageItemEvaluationTrace,
} from "#bb/lib/navigationPageState";
import { resolveNavigationTarget } from "#bb/lib/navigationTarget";
import { resolveSiteFileDownloadUrl } from "#bb/lib/pathContract";
import { ResourcePreloadKey } from "#bb/lib/resourcePreload";
import { buildTemplateContext, useTemplating } from "#bb/composables/useTemplating";
import type { ComputedRef } from "vue";

export interface NavigationPageRenderFeatures {
  editor?: boolean;
  resourceKey?: string;
  resourceDefinitionPath?: string | null;
  debugBuildingBlocks?: boolean;
}

const isExternalUrl = (value: string) =>
  /^(https?:)?\/\//.test(value)
  || value.startsWith("mailto:")
  || value.startsWith("tel:");

export function useNavigationPageItems(
  definitionData: ComputedRef<NavigationPageOption>,
  features: NavigationPageRenderFeatures,
  loggerName: string,
) {
  const logger = useLogger(loggerName);
  const debug = useBuildingBlockDebug(definitionData, features, loggerName);
  const report = inject<any | null>("report", null);
  const resourcePreload = inject(ResourcePreloadKey, null);
  const templateContext = computed(() => buildTemplateContext(report));
  const { applyTemplate } = useTemplating(templateContext, {
    logger,
  });
  const { getCachedAnswer } = useAnswers();
  const { getResolvedDataPath } = useContentManager();
  const conditionValues = useConditionValues(features.resourceKey || "default");
  const { resolveHref } = useAppRoute();
  const { siteId } = useSite();
  const config = useRuntimeConfig();
  const apiUrl = computed(() => {
    const publicConfig = config.public as Record<string, unknown>;
    const value = publicConfig.apiPublicUrl || publicConfig.apiUrl;
    return typeof value === "string" ? value : undefined;
  });

  async function loadConfiguredConditions() {
    if (features.editor || !resourcePreload?.loadConditions) return;
    const requirements = collectResourceConditionalRequirements([
      definitionData.value,
    ]);
    if (
      !requirements.userStates.length
      && !requirements.userMetrics.length
    ) {
      return;
    }

    debug.log("Loading conditional values", {
      resourceKey: features.resourceKey || "default",
      resourceDefinitionPath: features.resourceDefinitionPath ?? null,
      userStates: requirements.userStates,
      userMetrics: requirements.userMetrics,
    });
    try {
      await resourcePreload.loadConditions(requirements);
      debug.log("Conditional values loaded", {
        resourceKey: features.resourceKey || "default",
        userStateCount: requirements.userStates.length,
        userMetricCount: requirements.userMetrics.length,
      });
    } catch (error) {
      logger.error("Unable to load navigation page condition values", {
        error,
      });
    }
  }

  watch(
    definitionData,
    () => {
      if (import.meta.client) {
        void loadConfiguredConditions();
      }
    },
    { deep: true, immediate: true },
  );

  const resolveConditionValue = (source: ConditionSource) =>
    source.type === "answer"
      ? getCachedAnswer(
          source.blockId,
          getResolvedDataPath(source.blockId).join("/"),
        )?.value
      : conditionValues.resolveValue(source);
  const isConditionAvailable = (source: ConditionSource) =>
    source.type === "answer"
      ? getCachedAnswer(
          source.blockId,
          getResolvedDataPath(source.blockId).join("/"),
        ) !== null
      : conditionValues.isAvailable(source);

  const resolvedItems = computed(() =>
    (definitionData.value?.items ?? []).map(
      (sourceItem: NavigationPageItem, index: number) => {
        let evaluation: NavigationPageItemEvaluationTrace | null = null;
        const item = resolveNavigationPageItem(
          sourceItem,
          resolveConditionValue,
          isConditionAvailable,
          (trace) => {
            evaluation = trace;
          },
          definitionData.value?.displayStates,
        );
        const title = applyTemplate(item.title || "");
        const description = applyTemplate(item.description || "");
        const icon = applyTemplate(item.icon || "")
          || "lucide:arrow-right-circle";
        const iconColor = applyTemplate(item.iconColor || "");
        const image = applyTemplate(item.image || "");
        const url = applyTemplate(item.url || item.to || "");
        const target = resolveNavigationTarget(url, {
          resourceDefinitionPath: features.resourceDefinitionPath,
          resolvePath: resolveHref,
        });

        return {
          item: {
            ...item,
            id: item.id || `navigation-page-item-${index}`,
            title,
            description,
            icon,
            iconColor,
            image: resolveSiteFileDownloadUrl(
              image,
              apiUrl.value,
              siteId.value,
              features.resourceDefinitionPath,
            ),
            to: target?.href || "#",
            external: item.external ?? target?.external ?? isExternalUrl(url),
            disabled: !item.interactive || !url,
            classes: item.classes,
          },
          evaluation,
        };
      },
    ),
  );

  watch(
    resolvedItems,
    (resolved) => {
      for (const entry of resolved) {
        if (!entry.evaluation) continue;
        debug.log("Navigation item evaluated", {
          resourceKey: features.resourceKey || "default",
          resourceDefinitionPath: features.resourceDefinitionPath ?? null,
          item: {
            id: entry.item.id,
            title: entry.item.title,
            activeVariantId: entry.item.activeVariantId,
            displayStateId: entry.item.displayStateId,
            interactive: entry.item.interactive,
            disabled: entry.item.disabled,
            to: entry.item.to,
            external: entry.item.external,
          },
          evaluation: entry.evaluation,
        });
      }
    },
    { deep: true, immediate: true },
  );

  const items = computed(() => resolvedItems.value.map((entry) => entry.item));

  return { items };
}
